import { useAuthContext } from "@contexts/auth";
import { useDashboardUIActions, useDashboardUIContext } from "@contexts/dashboardUI";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { isBuildingTrial } from "@utils/checkPermiossion";
import { formatNumberToCurrency, formatNumberToLocale } from "@utils/formatNumber";
import { loadStorage, saveStorage } from "@utils/localStorageManager";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { orderFinancialSeries } from "./financialSeriesOrder";
import {
  AMPLI_CURVE_KEYS,
  AMPLI_SERIES_LABELS,
  AMPLI_CASH_PROJECTION_DESCRIPTION,
  filterCashFlowExport,
  filterCashFlowGraphSeries,
  isAmpliCurveVisible,
  isAmpliDisbursementPolicy,
  physicalFinancialLabel,
} from "./cashFlowPlanning";
import { resolveAmpliSeries, resolveCashProjectionSeries } from "./ampliSeries";
import { alignAccumulated, alignToAxis } from "./monthlySeries";
import {
  filterReceivablesExport,
  filterReceivablesSeries } from "./receivablesAccess";

// O rótulo também é a CHAVE de "série escondida" (`hiddenItens`), por isso vive numa
// constante: literal repetido é como o rótulo e a chave saem de sincronia.
const ADJUSTMENT_LABELS = Object.freeze({
  disbursement: "Desembolso com ajustes",
  added: "Movido para",
  removed: "Movido de",
});
// Duas gerações de rótulo já foram gravadas em `hiddenItens` (localStorage): a de
// "Adiantamento" e a de "Antecipação". Quem escondeu uma série continua com ela
// escondida — o rótulo é a CHAVE, então renomear sem migrar reexibe a série.
const LEGACY_ADJUSTMENT_LABELS = Object.freeze({
  "Projeção de caixa": AMPLI_SERIES_LABELS.cashProjection,
  "Desembolso com Adiantamento": ADJUSTMENT_LABELS.disbursement,
  "Adiantamento (Valor adicionado)": ADJUSTMENT_LABELS.added,
  "Adiantamento (Valor retirado)": ADJUSTMENT_LABELS.removed,
  "Desembolso com Antecipação": ADJUSTMENT_LABELS.disbursement,
  "Antecipação (Adicionada)": ADJUSTMENT_LABELS.added,
  "Antecipação (Retirada)": ADJUSTMENT_LABELS.removed,
});
export const normalizeHiddenFinancialLabels = (items) => [...new Set(
  (Array.isArray(items) ? items : []).map(
    (item) => LEGACY_ADJUSTMENT_LABELS[item] ?? item,
  ),
)];

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const colorArr = {
  allReceiv: "#1f1f1f",
  physical: "#ba52f2",
  paid: "#3acff7",
  toPay: "#fc9403",
  disbursement: "#6852F2",
  predicted: "#FFA07A",
  accumulatedDisbursement: "#FFD700",
  accumulatedPaid: "#FFD700",
  advancementTo: "#3CB371",
  advancementFrom: "#f27752",
  commited: "#2d8a77",
  balance: '#6eddbc',
  ampliDisbursement: "#2563EB",
  cashProjection: "#0F766E"
};
const reduceAdvancements = (taken, tooltipItem, mod) => {
  const list = taken.byItem.filter(i => {
    return i.data.some(j => j[mod] === tooltipItem.label)
  })
  const matches = []
  list.forEach((i) => {
    i.data.forEach((j) => {
      if (j[mod] === tooltipItem.label) {
        matches.push({
          ...j,
          label: i.name
        })
      }
    })
  })
  let response = []
  const totalAdv = matches.reduce((a, b) => a + b.value, 0)
  const revMod = mod === 'from' ? 'to' : 'from'
  matches.forEach(i => response.push(`${i.label}: ${formatNumberToCurrency(i.value)}, ${mod !== 'from' ? 'de' : 'para'} ${i[revMod]}`))

  return { totalAdv, response }
}
const FinancialBarChart = () => {

  const { activeProject } = useProjectContext();
  const { paidValues = [], disbursements = [], toPayValues = [], takenFrom = { byItem: [], byMonth: [] }, takenTo = { byItem: [], byMonth: [] }, followUps = [], accumulatedDisbursement = [], accumulatedPaid = [], physicals = [],
    hasPrevision, cashFlowPlanning } = activeProject;
  const ampliSeries = resolveAmpliSeries(activeProject)
  const cashProjectionSeries = resolveCashProjectionSeries(activeProject)
  // As duas séries só entram no gráfico quando a curva Ampli está `ready` ou
  // `stale` (curva anterior, ainda válida para exibição). Em `inconsistent`/
  // `unavailable` elas somem — nunca aparecem zeradas, o que pareceria
  // "Desembolso Ampli: R$ 0,00" quando na verdade não há curva nenhuma.
  const ampliCurveVisible = isAmpliCurveVisible(activeProject)
  const { barChartDate } = useDashboardUIContext()
  const { setBarChartDate, setIsLoading } = useDashboardUIActions()
  const { selectXlsxData, selectGraph } = useProjectActions()
  const { filterDates, selectedGraph, filterDocs } = useProjectContext()
  const [hiddenItens, setHiddenItens] = useState([])
  // Keep the selected time horizon without using any revenue series as padding.
  const selectedRangeDates = [];
    const rangeStart = new Date(filterDates.beginAt);
    const rangeEnd = new Date(filterDates.endAt);
  rangeStart.setDate(1);
  rangeEnd.setDate(1);
  rangeStart.setMonth(rangeStart.getMonth() + 1);
  rangeEnd.setMonth(rangeEnd.getMonth() + 1);

    while (rangeStart <= rangeEnd) {
    selectedRangeDates.push(`${String(rangeStart.getMonth() + 1).padStart(2, "0")}/${rangeStart.getFullYear()}`);
    rangeStart.setMonth(rangeStart.getMonth() + 1);
    }
  const { user: { configurations } } = useAuthContext();
  const { flux: fluxConfig = [] } = configurations?.dashboard?.graphs || {}
  const graphColors = configurations?.graphColors || colorArr
  const { projection: rawProjectionConfig = [], advancements: rawAdvancementsConfig = [], balance: rawBalanceConfig = [] } = configurations?.projection?.graphs || {}
  // The organization policy decides which planning curves belong on the projection chart.
  // Advancements and balance are not cash-flow planning charts: the policy may hide a
  // series there, never add one.
  const projectionConfig = filterCashFlowGraphSeries(rawProjectionConfig, cashFlowPlanning)
  const advancementsConfig = filterCashFlowGraphSeries(
    rawAdvancementsConfig,
    cashFlowPlanning,
    { ensurePlanningSeries: false },
  )
  const balanceConfig = filterCashFlowGraphSeries(
    rawBalanceConfig,
    cashFlowPlanning,
    { ensurePlanningSeries: false },
  )

  // O gráfico "advancementGraph" carrega o vocabulário de Antecipações, que não existe no
  // modo Ampli (spec D10). O efeito de mount já evita selecioná-lo, mas trocar de obra não
  // remonta o componente: se a seleção anterior era Antecipações, ela sobreviveria à troca
  // e a obra Ampli mostraria um gráfico sem nenhuma das suas séries. A coerção aqui é
  // idempotente e cobre os dois caminhos.
  const graphSet = isAmpliDisbursementPolicy(cashFlowPlanning) && selectedGraph === 'advancementGraph'
    ? 'projectionGraph'
    : selectedGraph
  const followUpDate = (point) => `${point.date.split("-")[1].padStart(2, "0")}/${point.date.split("-")[0]}`
  const scheduleCurve = (field) => followUps.map((point) => ({
    value: Number.isFinite(point?.[field]) ? point[field] : undefined,
    date: followUpDate(point),
  }))
  const scheduled = scheduleCurve('predictedValue')
  const baseCurve = scheduleCurve('baselineValue')
  const measuredCurve = scheduleCurve('measuredValue')
  const filter = (array, filters) => {
    return array.map(({ value, date }) => {
      const toReduce = filters?.filter(j => j.date === date).reduce((a, b) => a + b.value, 0)
      let newVal = value - toReduce
      if (newVal > -1 && newVal < 1) newVal = 0;
      if (toReduce) {
        return { value: newVal, date }
      }
      return { value, date }
    })
  }
  const filteredPaid = filter(paidValues, filterDocs.paidData)
  const filteredToPay = filter(toPayValues, filterDocs.toPayData)
  useEffect(() => {
    setIsLoading(true)
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [barChartDate])
  const [paidList, disbursementList, toPayList, takenFromList, takenToList, dates, physicalsList] = useMemo(() => {

    const paidDates = filteredPaid.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator;
    }, [])
    const disbursementDates = disbursements.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])
    const toPayDates = filteredToPay.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])
    const takenFromDates = takenFrom.byMonth.reduce((accumulator, item) => {
      accumulator.push(item.from)
      return accumulator
    }, [])
    const takenToDates = takenTo.byMonth.reduce((accumulator, item) => {
      accumulator.push(item.to)
      return accumulator
    }, [])
    const physicalsDates = physicals.reduce((accumulator, item) => {
      accumulator.push(item.date)
      return accumulator
    }, [])
    const ampliDates = ampliSeries.map((item) => item.date)
    const cashProjectionDates = cashProjectionSeries.map((item) => item.date)
    const followUpDates = followUps.map(followUpDate)
    const dates = [...selectedRangeDates, ...paidDates, ...disbursementDates, ...toPayDates, ...takenFromDates, ...takenToDates, ...physicalsDates, ...ampliDates, ...cashProjectionDates, ...followUpDates]
      // Ponto sem `date` NUNCA chega ao `split("/")` do comparador abaixo: um
      // `undefined` ali derruba a ordenação inteira (e com ela o gráfico) por causa
      // de uma única série incompleta. O back-end publica `date` em todas as séries
      // (`getFollowUps`, commit 078beb3); esta guarda existe para o payload antigo em
      // cache e para qualquer série nova que esqueça o campo.
      .filter(Boolean)
      .filter((item, index, self) => index === self.findIndex((indexedItem) => indexedItem === item))
      .sort((current, next) => {
        const [currentMonth, currentYear] = current.split("/")
        const [nextMonth, nextYear] = next.split("/")
        if (currentYear !== nextYear) {
          return Number(currentYear) - Number(nextYear);
        } else {
          return Number(currentMonth) - Number(nextMonth);
        }
      });
    const { paidMap, disbursementMap, toPayMap,
      takenFromMap, takenToMap, physicalsMap
    } = dates.reduce((acc, item) => {
      acc.paidMap.set(item, filteredPaid.find(paidItem => paidItem.date === item)?.value ?? 0)
      acc.disbursementMap.set(item, disbursements.find(disbursementItem => disbursementItem.date === item)?.value ?? 0)
      acc.toPayMap.set(item, filteredToPay.find(toPayItem => toPayItem.date === item)?.value ?? 0)
      acc.takenFromMap.set(item, takenFrom.byMonth.find(takenItem => takenItem.from === item)?.value ?? 0)
      acc.takenToMap.set(item, takenTo.byMonth.find(takenItem => takenItem.to === item)?.value ?? 0)
      acc.physicalsMap.set(item, physicals.find(physicalsItem => physicalsItem.date === item)?.value ?? 0)
      return acc;
    }, { paidMap: new Map(), disbursementMap: new Map(), toPayMap: new Map(),
      takenFromMap: new Map(), takenToMap: new Map(), physicalsMap: new Map()
    });
    return [Array.from(paidMap.values()), Array.from(disbursementMap.values()), Array.from(toPayMap.values()), Array.from(takenFromMap.values()), Array.from(takenToMap.values()), dates, Array.from(physicalsMap.values())];
  }, [filteredPaid, disbursements, filteredToPay, takenFrom, takenTo, physicals, followUps, ampliSeries, cashProjectionSeries]);
  const onBarClick = (event, itemsClicked) => {
    if (!event.chart.tooltip.dataPoints) return
    if (itemsClicked) {
      const selectedDate = event.chart.tooltip.dataPoints[0]?.label
      if (selectedDate == barChartDate) {
        setBarChartDate();
      } else {
        setBarChartDate(selectedDate)
      }
    } else {
      setBarChartDate();
    }
  }

  // ⚠️ O eixo do gráfico é `dates` (é o que vai em `labels`), e cada série é
  // consumida POR ÍNDICE. Então toda série tem de ser alinhada a `dates` —
  // qualquer outra base desloca os pontos. Duas fontes de desalinhamento havia aqui:
  //
  //  1. séries montadas com `.map()` sobre o array de dados cru (`receivedBills`,
  //     `toReceiveBills`): em ordem de chegada e só com as competências que têm
  //     valor. Caso real da Prívia — R$ 206.800,00 em jun/2024 e R$ 187.095,71 em
  //     abr/2025 apareciam em abr/2024 e mai/2024, os dois primeiros índices;
  //  2. séries alinhadas a `allDates` (o range do filtro) enquanto o eixo é
  //     `dates` (união das competências com dado). `dates` ⊇ `allDates`, então a
  //     partir da primeira divergência tudo escorregava.
  //
  // `alignToAxis`/`alignAccumulated` (./monthlySeries) resolvem por competência.
  const accumulatedDisbursementList = alignAccumulated(dates, accumulatedDisbursement)
  const accumulatedPaidList = alignAccumulated(dates, accumulatedPaid)
  const ampliDisbursementList = alignToAxis(dates, ampliSeries)
  // `fallback: undefined` (e não 0) porque a desembolso projetado é uma LINHA: mês sem valor
  // tem que virar lacuna no traçado, senão a linha despenca até R$ 0,00 e volta, afirmando
  // um caixa zerado que ninguém projetou. As barras do desembolso mantêm o 0.
  const cashProjectionList = alignToAxis(dates, cashProjectionSeries, { fallback: undefined })
  const scheduledList = alignToAxis(dates, scheduled, { fallback: undefined, aggregate: "last" })
  const baseList = alignToAxis(dates, baseCurve, { fallback: undefined, aggregate: "last" })
  const measuredList = alignToAxis(dates, measuredCurve, { fallback: undefined, aggregate: "last" })
  const buildMonthly = (valueField) => {
    const perFollowUp = followUps.map(i => ({
      value: Number.isFinite(i[valueField]) ? i[valueField] : undefined,
      date: `${i.date.split("-")[1].padStart(2, "0")}/${i.date.split("-")[0]}`,
    }))
    return alignToAxis(dates, perFollowUp, { fallback: undefined, aggregate: "last" })
  }
  const measuredMonthlyList = buildMonthly('measuredDeltaValue')
  const commitedList = dates.map((i, idx) => {
    return paidList[idx] + toPayList[idx]
  })
  const disbWithAdvanceList = disbursementList.map((i, idx) => i + takenToList[idx] - takenFromList[idx])
  const checkGraphConfig = i => {
    switch (i) {
      case 'accumulatedDisbursement':
        return {
          label: 'Desembolso Acumulado',
          borderColor: graphColors.disbursement,
          backgroundColor: graphColors.disbursement,
          type: 'line',
          data: accumulatedDisbursementList,
          stack: 'disbursement',
          hidden: hiddenItens.includes('Desembolso Acumulado'),
        }
      case 'accumulatedPaid':
        return {
          label: 'Pago Acumulado',
          borderColor: graphColors.paid,
          backgroundColor: graphColors.paid,
          type: 'line',
          data: accumulatedPaidList,
          stack: 'paid',
          hidden: hiddenItens.includes('Pago Acumulado'),
        }
      case 'commited':
        return {
          label: "Comprometido",
          borderColor: graphColors.commited,
          backgroundColor: graphColors.commited,
          data: commitedList,
          stack: 'commited',
          hidden: hiddenItens.includes('Comprometido'),
        }
      case 'disbursement':
        return {
          label: "Desembolso",
          borderColor: graphColors.disbursement,
          backgroundColor: graphColors.disbursement,
          data: disbursementList,
          stack: 'disbursement',
          hidden: hiddenItens.includes('Desembolso'),
        }
      case 'ampliDisbursement':
        return {
          label: AMPLI_SERIES_LABELS.ampliDisbursement,
          borderColor: graphColors.ampliDisbursement ?? colorArr.ampliDisbursement,
          backgroundColor: graphColors.ampliDisbursement ?? colorArr.ampliDisbursement,
          data: ampliDisbursementList,
          stack: 'ampliDisbursement',
          hidden: hiddenItens.includes(AMPLI_SERIES_LABELS.ampliDisbursement),
        }
      case 'cashProjection':
        return {
          label: AMPLI_SERIES_LABELS.cashProjection,
          borderColor: graphColors.cashProjection ?? colorArr.cashProjection,
          backgroundColor: graphColors.cashProjection ?? colorArr.cashProjection,
          data: cashProjectionList,
          type: 'line',
          stack: 'cashProjection',
          hidden: hiddenItens.includes(AMPLI_SERIES_LABELS.cashProjection),
        }
      case 'disbWithAdvance':
        return {
          label: ADJUSTMENT_LABELS.disbursement,
          borderColor: graphColors.predicted,
          backgroundColor: graphColors.predicted,
          data: disbWithAdvanceList,
          stack: 'disbursementWithAdv',
          hidden: hiddenItens.includes(ADJUSTMENT_LABELS.disbursement),
        }
      case 'paid':
        return {
          label: "Pago",
          borderColor: graphColors.paid,
          backgroundColor: graphColors.paid,
          data: paidList,
          stack: 'paid',
          hidden: hiddenItens.includes('Pago'),
        }
      case 'physicals':
        return {
          label: "Físico Financeiro",
          borderColor: graphColors.physical,
          backgroundColor: graphColors.physical,
          data: physicalsList,
          stack: 'physicals',
          hidden: hiddenItens.includes('Físico Financeiro'),
        }
      case 'scheduled':
        return {
          label: 'Previsto',
          borderColor: graphColors.predicted,
          backgroundColor: graphColors.predicted,
          type: 'line',
          data: scheduledList,
          stack: 'predicted',
          hidden: hiddenItens.includes('Previsto'),
        }
      case 'base':
        return {
          label: 'Linha de base',
          borderColor: '#FFD700',
          backgroundColor: '#FFD700',
          type: 'line',
          borderDash: [6, 4],
          data: baseList,
          stack: 'base',
          hidden: hiddenItens.includes('Linha de base'),
        }
      case 'measured':
        return {
          label: 'Medido acumulado',
          borderColor: '#16A34A',
          backgroundColor: '#16A34A',
          type: 'line',
          data: measuredList,
          stack: 'measured',
          hidden: hiddenItens.includes('Medido acumulado'),
        }
      case 'measuredMonthly':
        return {
          label: 'Medido mensal',
          borderColor: '#16A34A',
          backgroundColor: '#16A34A',
          data: measuredMonthlyList,
          stack: 'measuredMonthly',
          hidden: hiddenItens.includes('Medido mensal'),
        }
      case 'takenFrom':
        return {
          label: ADJUSTMENT_LABELS.removed,
          borderColor: graphColors.advancementFrom,
          backgroundColor: graphColors.advancementFrom,
          data: takenFromList.map(i => i * -1),
          stack: 'disbursement',
          hidden: hiddenItens.includes(ADJUSTMENT_LABELS.removed),
        }
      case 'takenTo':
        return {
          label: ADJUSTMENT_LABELS.added,
          borderColor: graphColors.advancementTo,
          backgroundColor: graphColors.advancementTo,
          data: takenToList,
          stack: 'disbursement',
          hidden: hiddenItens.includes(ADJUSTMENT_LABELS.added),
        }
      case 'toPay':
        return {
          label: "A pagar",
          borderColor: graphColors.toPay,
          backgroundColor: graphColors.toPay,
          data: toPayList,
          stack: 'paid',
          hidden: hiddenItens.includes('A pagar'),
        }
      default:
        break;
    }
  };

  // Prevision buildings always show the monthly "Medido" bar in the projeção
  // chart by default, without a per-user toggle. Non-Prevision buildings never
  // get it here (so it never falls back to the Sienge measured on the chart).
  // As duas séries Ampli só ficam disponíveis para seleção quando a curva está
  // pronta (`ready`/`stale`); em `inconsistent`/`unavailable` a chave nem chega
  // ao `checkGraphConfig`, então não existe um dataset "zerado" no ar.
  const filterAmpliCurveKeys = (keys) => (
    ampliCurveVisible ? keys : keys.filter((key) => !AMPLI_CURVE_KEYS.includes(key))
  )
  const graphs = {
    projectionGraph: filterReceivablesSeries(filterAmpliCurveKeys(orderFinancialSeries(projectionConfig, { hasPrevision }))).map(i => {
      return checkGraphConfig(i)
    }),
    fluxGraph: filterReceivablesSeries(filterAmpliCurveKeys(fluxConfig)).map(i => {
      return checkGraphConfig(i)
    }),
    advancementGraph: filterReceivablesSeries(filterAmpliCurveKeys(orderFinancialSeries(advancementsConfig, { hasPrevision }))).map(i => {
      return checkGraphConfig(i)
    }),
    balanceGraph: filterReceivablesSeries(filterAmpliCurveKeys(balanceConfig)).map(i => {
      return checkGraphConfig(i)
    })
  };
  const handleLegendClick = (e, item) => {
    const { text } = item
    setHiddenItens(prev => {
      const updatedHiddenItems = prev.includes(text)
        ? prev.filter(i => i !== text)
        : [...prev, text];

      saveStorage('hiddenItens', updatedHiddenItems);
      return updatedHiddenItems;
    });
  }
  const graphOptions = {
    scales: {
      x: {
        stacked: true,
        display: true,
        title: {
          display: true,
          text: 'Mês/Ano',
          font: {
            weight: 'bold'
          }
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Valor',
          font: {
            weight: 'bold'
          }
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    onClick: onBarClick,
    plugins: {
      legend: {
        position: "top",
        display: true,
        onClick: handleLegendClick
      },
      title: {
        display: false,
        text: "Projeção Financeira",
        align: "start",
        color: "black",
        font: {
          size: "18px"
        }
      },
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset;
            if (dataset.label === AMPLI_SERIES_LABELS.cashProjection) {
              return [`${dataset.label}: ${tooltipItem.formattedValue}`, AMPLI_CASH_PROJECTION_DESCRIPTION];
            }
            if (tooltipItem.dataset.label === ADJUSTMENT_LABELS.added) {
              const { totalAdv, response } = reduceAdvancements(takenTo, tooltipItem, 'to')
              return [`Total: ${formatNumberToLocale(totalAdv)}`, ...response]
            }
            if (tooltipItem.dataset.label === ADJUSTMENT_LABELS.removed) {
              const { totalAdv, response } = reduceAdvancements(takenFrom, tooltipItem, 'from')
              return [`Total: ${formatNumberToLocale(totalAdv)}`, ...response]
            }
            if (tooltipItem.dataset.label === 'Desembolso') {
              if (takenFrom.byItem.filter(({ data }) => data.some(({ from }) => from === tooltipItem.label)).length > 0) {
                const { totalAdv } = reduceAdvancements(takenFrom, tooltipItem, 'from')
                return [`Desembolso: ${tooltipItem.formattedValue}`, `Desembolso - ajustes: ${formatNumberToLocale(tooltipItem.raw - totalAdv)}`]
              }
              if (takenTo.byItem.filter(({ data }) => data.some(({ to }) => to === tooltipItem.label)).length > 0) {
                const { totalAdv } = reduceAdvancements(takenTo, tooltipItem, 'to')
                return [`Desembolso: ${tooltipItem.formattedValue}`, `Desembolso + ajustes: ${formatNumberToLocale(tooltipItem.raw + totalAdv)}`]
              }
            }
            return `${dataset.label}: ${tooltipItem.formattedValue}`;
          }
        }
      }
    }
  };
  useEffect(() => {
    if (window.location.pathname.includes('projecao-financeira')) {
      // O gráfico "advancementGraph" carrega o vocabulário de Antecipações
      // (Desembolso com Antecipação, takenTo/takenFrom). No modo Ampli esse
      // conceito não existe (spec D10), então ele não é oferecido — o gráfico
      // "projectionGraph" (sem antecipações) entra no lugar.
      selectGraph(isAmpliDisbursementPolicy(cashFlowPlanning) ? 'projectionGraph' : 'advancementGraph')
    } else {
      selectGraph('fluxGraph')
    }
    setHiddenItens(normalizeHiddenFinancialLabels(loadStorage('hiddenItens')))
  }, [])
  useEffect(() => {
    let fields, resources;
    if (graphSet === 'balanceGraph') {
      fields = [
        { label: 'Data', key: 'date' },
        { label: 'Comprometido', key: 'commited' }];
      resources = dates.map((date, index) => ({
        date,
        commited: commitedList[index]
      }));
    } else if (graphSet === 'fluxGraph') {
      fields = [
        { label: 'Data', key: 'date' },
        { label: 'Previsto', key: 'scheduled' },
        { label: 'Desembolso Acumulado', key: 'accumulatedDisbursement' },
        { label: 'Pago Acumulado', key: 'accumulatedPaid' }];
      resources = dates.map((date, index) => ({
        date,
        scheduled: scheduledList[index],
        accumulatedDisbursement: accumulatedDisbursementList[index],
        accumulatedPaid: accumulatedPaidList[index]
      }));
    } else if (graphSet === 'projectionGraph') {
      fields = [
        { label: 'Data', key: 'date' },
        { label: 'Físico Financeiro', key: 'physicals' },
        { label: 'Desembolso', key: 'disbursement' },
        { label: ADJUSTMENT_LABELS.disbursement, key: 'disbWithAdvance' },
        { label: 'Medido', key: 'measuredMonthly' },
        { label: 'Pago', key: 'paid' },
        { label: 'A pagar', key: 'toPay' },
      // As duas séries da curva Ampli só entram na exportação quando há curva pra
        // mostrar (mesmo gate do gráfico, `isAmpliCurveVisible`) — `filterCashFlowExport`
        // abaixo já cuida de esconder as colunas de Sienge/Antecipação fora do modo Ampli.
        ...(ampliCurveVisible ? [
          { label: AMPLI_SERIES_LABELS.ampliDisbursement, key: 'ampliDisbursement' },
          { label: AMPLI_SERIES_LABELS.cashProjection, key: 'cashProjection' },
        ] : [])];
      resources = dates.map((date, index) => ({
        date,
        physicals: physicalsList[index],
        disbursement: disbursementList[index],
        disbWithAdvance: disbWithAdvanceList[index],
        measuredMonthly: measuredMonthlyList[index],
        paid: paidList[index],
        toPay: toPayList[index],
        ...(ampliCurveVisible ? {
          ampliDisbursement: ampliDisbursementList[index],
          cashProjection: cashProjectionList[index],
        } : {})
      }));
    } else if (graphSet === 'advancementGraph') {
      fields = [
        { label: 'Data', key: 'date' },
        { label: 'Físico Financeiro', key: 'physicals' },
        { label: 'Desembolso', key: 'disbursement' },
        { label: ADJUSTMENT_LABELS.disbursement, key: 'disbWithAdvance' },
        { label: 'Medido', key: 'measuredMonthly' },
        { label: 'Pago', key: 'paid' },
        { label: 'A pagar', key: 'toPay' },
        { label: ADJUSTMENT_LABELS.added, key: 'takenTo' },
        { label: ADJUSTMENT_LABELS.removed, key: 'takenFrom' }];
      resources = dates.map((date, index) => ({
        date,
        physicals: physicalsList[index],
        disbursement: disbursementList[index],
        disbWithAdvance: disbWithAdvanceList[index],
        measuredMonthly: measuredMonthlyList[index],
        paid: paidList[index],
        toPay: toPayList[index],
        takenTo: takenToList[index],
        takenFrom: takenFromList[index]
      }));
    }

    ({ fields, resources } = filterReceivablesExport(
      { fields, resources }));

    ({ fields, resources } = filterCashFlowExport(
      { fields, resources },
      cashFlowPlanning,
    ));

    selectXlsxData({ fields, resources })
  }, [selectedGraph, cashFlowPlanning, ampliCurveVisible]);
  if (graphSet === 'projectionGraph') {
    return <>
      <Bar options={graphOptions}
      data={{
        labels: dates,
        datasets: !isBuildingTrial() ? graphs.projectionGraph : [
          {
            label: "Desembolso",
            borderColor: "#6852F2",
            backgroundColor: "#6852F2",
            data: disbursementList,
            stack: 'disbursement'
          },

        ]
      }} />
    </>
  } else if (graphSet === 'advancementGraph') {
    return <>
      <Bar options={graphOptions}
      data={{
        labels: dates,
        datasets: !isBuildingTrial() ? graphs.advancementGraph : [
          {
            label: "Desembolso",
            borderColor: "#6852F2",
            backgroundColor: "#6852F2",
            data: disbursementList,
            stack: 'disbursement'
          },
        ]
      }} />
    </>
  } else if (graphSet === 'balanceGraph') {
    return <>
      <Bar options={graphOptions}
      data={{
        labels: dates,
        datasets: !isBuildingTrial() ? graphs.balanceGraph : [
          {
            label: "Desembolso",
            borderColor: "#6852F2",
            backgroundColor: "#6852F2",
            data: disbursementList,
            stack: 'disbursement'
          },
        ]
      }} />
    </>
  } else {
    return <>
      <Line options={graphOptions}
      data={{
        labels: dates,
        datasets: !isBuildingTrial() ? graphs.fluxGraph : [
          {
            label: "Desembolso",
            borderColor: "#6852F2",
            backgroundColor: "#6852F2",
            data: disbursementList,
            stack: 'disbursement'
          },

        ]
      }} />
    </>

  }
};
export default FinancialBarChart;
